// Utilidad para normalizar y formatear RUT chileno
// Entradas aceptan con o sin puntos/guión, mayúsculas o minúsculas
// Salida: Formato estándar con puntos y guión: 12.345.678-9
// Si no es posible validar mínimamente la estructura, retorna la entrada original

/**
 * Normaliza un RUT eliminando puntos y guión y pasando a minúsculas
 * @param value RUT de entrada (ej: 12.345.678-9)
 * @returns string sin separadores (ej: 123456789)
 */
export function normalizeRut(value: string): string {
  return (value || '').replace(/\./g, '').replace(/-/g, '').trim().toLowerCase();
}

/**
 * Aplica formato con puntos y guión a un RUT normalizado o crudo.
 * No valida dígito verificador; solo formatea estructura cuando es posible.
 * @param value RUT de entrada (ej: 123456789 o 12.345.678-9)
 * @returns RUT formateado (ej: 12.345.678-9) o valor original si no se puede formatear.
 */
export function formatRut(value: string): string {
  if (!value) return '';
  const raw = normalizeRut(value);
  if (raw.length < 2) return value; // Debe tener al menos cuerpo + dv

  const cuerpo = raw.slice(0, -1);
  const dv = raw.slice(-1);
  if (!/^\d+$/.test(cuerpo)) return value; // cuerpo debe ser numérico

  // Insertar puntos cada 3 desde el final
  let cuerpoConPuntos = '';
  for (let i = 0, len = cuerpo.length; i < len; i++) {
    const char = cuerpo[len - 1 - i];
    cuerpoConPuntos = char + cuerpoConPuntos;
    if ((i + 1) % 3 === 0 && i !== len - 1) {
      cuerpoConPuntos = '.' + cuerpoConPuntos;
    }
  }
  return `${cuerpoConPuntos}-${dv}`.toUpperCase();
}

/**
 * Formatea un RUT sin separadores de miles, solo cuerpo-DV (ej: 12345678-9).
 * A diferencia de formatRut(), no inserta puntos.
 * @param value RUT de entrada (ej: 123456789 o 12.345.678-9)
 * @returns RUT formateado sin puntos (ej: 12345678-9) o valor original si no se puede formatear.
 */
export function formatRutWithoutDots(value: string): string {
  if (!value) return '';
  const raw = normalizeRut(value);
  if (raw.length < 2) return value;

  const cuerpo = raw.slice(0, -1);
  const dv = raw.slice(-1);
  if (!/^\d+$/.test(cuerpo)) return value;

  return `${cuerpo}-${dv}`.toUpperCase();
}

export default formatRut;
